import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import PdfViewer from "./PdfViewer";
import {
  Public,
  MoreVert,
  ThumbUpOutlined,
  FavoriteBorderOutlined,
  SentimentSatisfiedOutlined,
  ChatBubbleOutlineOutlined,
  ShareOutlined,
  Close,
} from "@mui/icons-material";
import CreatePost from "./CreatePost";
import { API_BASE } from "../services/authService";
import { apiGet, apiPost, apiFetch } from "../api/apiClient";
import { FormattedText } from "../utils/textFormatter";
import { getUserName } from "../utils/userInfo";

export default function MainFeed({
  userName,
  userInitials,
  userProfile,
  onAddPostExternal,
}) {
  const [posts, setPosts] = useState([]);
  const [postComments, setPostComments] = useState({});
  const [photoPopup, setPhotoPopup] = useState({ open: false, imageUrl: "" });

  const handlePhotoClick = (imageUrl) => {
    setPhotoPopup({ open: true, imageUrl });
  };

  const handlePhotoClose = () => {
    setPhotoPopup({ open: false, imageUrl: "" });
  };

  // Fetch comments for each post when posts change
  useEffect(() => {
    posts.forEach(async (post) => {
      try {
        const comments = await apiGet(`/posts/${post.id}/comments`);
        setPostComments((prev) => ({ ...prev, [post.id]: comments }));
      } catch (e) {
        console.warn(`Failed to load comments for post ${post.id}`, e);
      }
    });
  }, [posts]);
  const [commentTexts, setCommentTexts] = useState({});
  const [mediaUrlMap, setMediaUrlMap] = useState({}); // originalUrl -> objectUrl
  const objectUrlsRef = useRef(new Set());

  const apiHost = API_BASE.replace(/\/api$/, "");

  const toAbsoluteUrl = useCallback(
    (u) => {
      if (!u) return null;
      if (/^https?:\/\//i.test(u)) return u;
      if (u.startsWith("/")) return `${apiHost}${u}`;
      return `${apiHost}/${u}`;
    },
    [apiHost]
  );

  const timeAgoFrom = (iso) => {
    try {
      const then = new Date(iso);
      const diff = Date.now() - then.getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "now";
      if (mins < 60) return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h`;
      const days = Math.floor(hrs / 24);
      return `${days}d`;
    } catch {
      return "now";
    }
  };

  const transformPost = useCallback(
    (p) => {
      // Handle server shape with employee object
      if (p && Array.isArray(p.media)) {
        const attachments = p.media.map((m) => ({
          url: toAbsoluteUrl(m.file_url),
          type: m.file_type || undefined,
        }));
        let author = "Unknown";
        let authorInitials = "U";
        let role = "";
        let company = "";
        let email = "";
        if (p.employee) {
          author =
            p.employee.employee_name || p.employee.employee_code || "Unknown";
          authorInitials = (
            p.employee.employee_name ||
            p.employee.employee_code ||
            "U"
          )
            .split(" ")
            .map((w) => w[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();
          role = p.employee.designation_id || "";
          company = p.employee.department_name || "";
          email = p.employee.office_email || "";
        }
        return {
          id: p.id,
          author,
          authorInitials,
          role,
          company,
          email,
          timeAgo: timeAgoFrom(p.created_at),
          content: p.content,
          likes: p.likes || 0,
          comments: p.comments || 0,
          isLiked: false,
          likedBy: [],
          attachments,
          raw: p,
          reactionCounts: p.reactionCounts || { like: 0, love: 0, haha: 0 },
        };
      }
      // Handle local/fallback shape from CreatePost
      if (p && (p.attachments || p.author)) {
        return {
          id: p.id,
          author: p.author || userName || "Me",
          authorInitials: p.authorInitials || userInitials || "ME",
          role: p.role || userProfile?.role || "",
          company: p.company || userProfile?.company || "",
          timeAgo: p.timeAgo || "now",
          content: p.content,
          likes: p.likes || 0,
          comments: p.comments || 0,
          isLiked: !!p.isLiked,
          likedBy: p.likedBy || [],
          attachments: (p.attachments || []).map((a) => ({
            url: toAbsoluteUrl(a.url || a),
          })),
          raw: p,
        };
      }
      return p;
    },
    [toAbsoluteUrl, userInitials, userName, userProfile]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGet('/posts');
        const mapped = Array.isArray(data) ? data.map(transformPost) : [];
        setPosts(mapped);
      } catch (e) {
        console.error("Error loading posts", e);
        setPosts([]);
      }
    };
    load();
  }, [transformPost]);

  // Resolve media URLs that are on the API host (or relative) by fetching with auth and creating blob URLs
  useEffect(() => {
    const controller = new AbortController();
    const resolveUrls = async () => {
      const allUrls = [];
      posts.forEach((p) => {
        (p.attachments || []).forEach((a) => {
          const u = a?.url || a;
          if (!u) return;
          const abs = toAbsoluteUrl(u);
          // Only resolve if served by API host (same backend) to avoid CORP issues
          const shouldResolve = abs.startsWith(apiHost);
          if (shouldResolve) allUrls.push({ original: abs });
        });
      });

      const tasks = allUrls
        .filter(({ original }) => !mediaUrlMap[original])
        .map(async ({ original }) => {
          try {
            const res = await apiFetch(original, {
              signal: controller.signal,
            });
            const blob = await res.blob();
            const objUrl = URL.createObjectURL(blob);
            objectUrlsRef.current.add(objUrl);
            return { original, objUrl };
          } catch (e) {
            console.warn("Failed to resolve media url", original, e);
            return null;
          }
        });

      const results = await Promise.all(tasks);
      const updates = {};
      results.forEach((r) => {
        if (r) updates[r.original] = r.objUrl;
      });
      if (Object.keys(updates).length) {
        setMediaUrlMap((prev) => ({ ...prev, ...updates }));
      }
    };
    resolveUrls();
    return () => {
      controller.abort();
    };
  }, [posts, toAbsoluteUrl, apiHost, mediaUrlMap]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    const setRef = objectUrlsRef.current;
    return () => {
      setRef.forEach((u) => URL.revokeObjectURL(u));
      setRef.clear();
    };
  }, []);

  const onAddPost = (post) => {
    const mapped = transformPost(post);
    setPosts((prev) => [mapped, ...prev]);
    onAddPostExternal?.(mapped);
  };

  const handleReaction = async (postId, type) => {
    try {
      await apiPost(`/posts/${postId}/reaction`, { type });
      console.log(`Reaction ${type} added to post ${postId}`);
    } catch (e) {
      console.error("Failed to react", e);
    }
  };

  const handleComment = async (postId) => {
    const commentText = commentTexts[postId] || "";
    if (!commentText.trim()) return;
    const userName = getUserName();
    try {
      await apiPost(`/posts/${postId}/comment`, { comment: commentText });
      setPostComments((prev) => ({
        ...prev,
        [postId]: [
          ...(prev[postId] || []),
          {
            id: Date.now(),
            employee_name: userName,
            comment: commentText,
            timeAgo: "now",
          },
        ],
      }));
      setCommentTexts((prev) => ({ ...prev, [postId]: "" }));
    } catch (e) {
      console.error("Failed to add comment", e);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: "100%",
        height: "100%",
        marginTop: "64px",
        p: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {/* <Toolbar /> removed, marginTop replaces it */}
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <CreatePost
          userName={userName}
          userInitials={userInitials}
          userProfile={userProfile}
          onAddPost={onAddPost}
        />
        {posts.map((post) => (
          <Card
            key={post.id}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              width: "100%",
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* ...existing post card code... */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#FF0000",
                    width: 30,
                    height: 30,
                    fontSize: "0.9rem",
                  }}
                >
                  {post.authorInitials}
                </Avatar>
                <Box sx={{ flexGrow: 1, textAlign: "left" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      color: "#0D004A",
                      textAlign: "left",
                      fontFamily: "Roboto, sans-serif",
                    }}
                  >
                    {post.author} {post.role && <>| {post.role}</>}{" "}
                    {post.company && <>| {post.company}</>}
                  </Typography>
                  {post.email && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textAlign: "left",
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {post.email}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      textAlign: "left",
                      fontFamily: "Roboto, sans-serif",
                    }}
                  >
                    {post.timeAgo} • <Public sx={{ fontSize: 14 }} />
                  </Typography>
                </Box>
                <IconButton>
                  <MoreVert />
                </IconButton>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  lineHeight: 1.6,
                  textAlign: "left",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                <FormattedText text={post.content} />
              </Typography>
              {/* Attachments */}
              {Array.isArray(post.attachments) &&
                post.attachments.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      mb: 2,
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: 1,
                    }}
                  >
                    {post.attachments.map((att, idx) => {
                      const raw = att.url || att;
                      const abs = toAbsoluteUrl(raw);
                      const url = abs;

                      const lower = (url || "").toLowerCase();
                      const isImage = /(\.png|\.jpg|\.jpeg|\.gif|\.webp)$/.test(
                        lower
                      );
                      const isVideo = /(\.mp4|\.webm|\.ogg)$/.test(lower);
                      const isPdf = lower.endsWith(".pdf");

                      if (isImage) {
                        return (
                          <Box
                            key={idx}
                            sx={{
                              width: "100%",
                              height: 500,
                              backgroundColor: "#000",
                              borderRadius: 2,
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={url}
                              alt="attachment"
                              crossOrigin="anonymous"
                              onClick={() => handlePhotoClick(url)}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                width: "auto",
                                height: "auto",
                                objectFit: "contain",
                                cursor: "pointer",
                                transition: "transform 0.2s ease-in-out",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.transform = "scale(1.02)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.transform = "scale(1)")
                              }
                            />
                          </Box>
                        );
                      }
                      if (isVideo) {
                        return (
                          <Box
                            key={idx}
                            sx={{
                              width: "100%",
                              height: 500,
                              backgroundColor: "#000",
                              borderRadius: 2,
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <video
                              src={url}
                              crossOrigin="anonymous"
                              controls
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </Box>
                        );
                      }
                      if (isPdf) {
                        return (
                          <PdfViewer
                            key={idx}
                            fileUrl={url}
                            fileName={att.name || `document-${idx + 1}.pdf`}
                            maxWidth="100%"
                            minHeight={500}
                            showNavigation={true}
                            clickToOpen={true}
                          />
                        );
                      }
                      return (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2" }}
                        >
                          Download
                        </a>
                      );
                    })}
                  </Box>
                )}
              <Box
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between",
                  mb: 2,
                  pt: 1
                }}
              >
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  <Button
                    startIcon={<ThumbUpOutlined sx={{ fontSize: 18 }} />}
                    onClick={() => handleReaction(post.id, "like")}
                    sx={{
                      color: "#0a66c2",
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "rgba(10, 102, 194, 0.08)",
                      },
                    }}
                  >
                    Like {post.reactionCounts?.like > 0 && `(${post.reactionCounts.like})`}
                  </Button>
                  <Button
                    startIcon={<FavoriteBorderOutlined sx={{ fontSize: 18 }} />}
                    onClick={() => handleReaction(post.id, "love")}
                    sx={{
                      color: "#df704d",
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "rgba(223, 112, 77, 0.08)",
                      },
                    }}
                  >
                    Love {post.reactionCounts?.love > 0 && `(${post.reactionCounts.love})`}
                  </Button>
                  <Button
                    startIcon={<SentimentSatisfiedOutlined sx={{ fontSize: 18 }} />}
                    onClick={() => handleReaction(post.id, "celebrate")}
                    sx={{
                      color: "#f5a623",
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "rgba(245, 166, 35, 0.08)",
                      },
                    }}
                  >
                    Celebrate {post.reactionCounts?.celebrate > 0 && `(${post.reactionCounts.celebrate})`}
                  </Button>
                  <Button
                    startIcon={<ChatBubbleOutlineOutlined sx={{ fontSize: 18 }} />}
                    onClick={() => {}}
                    sx={{
                      color: "#0a66c2",
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "rgba(10, 102, 194, 0.08)",
                      },
                    }}
                  >
                    Comment {postComments[post.id]?.length > 0 && `(${postComments[post.id].length})`}
                  </Button>
                  <Button
                    startIcon={<ShareOutlined sx={{ fontSize: 18 }} />}
                    onClick={() => {}}
                    sx={{
                      color: "#0a66c2",
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "rgba(10, 102, 194, 0.08)",
                      },
                    }}
                  >
                    Share
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {/* Comments Section */}
              <Box sx={{ mt: 2 }}>
                {postComments[post.id]?.map((comment) => {
                  console.log(comment);

                  const commentAuthor =
                    comment.employee_name || comment.author || "Unknown";
                  const commentInitials = commentAuthor
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();
                  return (
                    <Box
                      key={comment.id}
                      sx={{ display: "flex", textAlign: "left", gap: 2, mb: 1 }}
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: "#FF0000",
                          fontSize: "0.8rem",
                        }}
                      >
                        {commentInitials}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            color: "#0D004A",
                            fontFamily: "Roboto, sans-serif",
                          }}
                        >
                          {commentAuthor}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#666",
                            fontFamily: "Roboto, sans-serif",
                          }}
                        >
                          <FormattedText
                            text={comment.comment || comment.text}
                          />
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: "#FF0000",
                      fontSize: "0.8rem",
                    }}
                  >
                    {userInitials}
                  </Avatar>
                  <TextField
                    placeholder="Add a comment..."
                    size="small"
                    fullWidth
                    value={commentTexts[post.id] || ""}
                    onChange={(e) =>
                      setCommentTexts((prev) => ({
                        ...prev,
                        [post.id]: e.target.value,
                      }))
                    }
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleComment(post.id);
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8f9fa",
                        borderRadius: 2,
                        "& fieldset": { border: "none" },
                      },
                    }}
                  />
                  <Button
                    onClick={() => handleComment(post.id)}
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontFamily: "Roboto, sans-serif",
                      backgroundColor: "red",
                    }}
                  >
                    Post
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Photo Popup Dialog */}
      <Dialog
        open={photoPopup.open}
        onClose={handlePhotoClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              maxWidth: "100%",
              maxHeight: "90vh",
              overflow: "hidden",
            }}
          >
            <img
              crossOrigin="anonymous"
              src={photoPopup.imageUrl}
              alt="Full size"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: 8,
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              }}
            />
            <IconButton
              onClick={handlePhotoClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(0,0,0,0.5)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.7)",
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
